import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { readConsent, writeConsent } from '../lib/consent'

/**
 * Cookie consent banner. Storage contract lives in ../lib/consent.
 *
 * Analytics stays denied until someone actively chooses otherwise — no decision
 * is not consent (GDPR Art. 4(11)). "Only necessary" carries the same visual
 * weight as "Accept"; a reject button that is harder to reach is a dark pattern.
 * It never blocks the funnel, so it is not a cookie wall.
 *
 * This matters more here than in the other apps: the funnel is the surface most
 * likely to get attribution tracking, and `hasAnalyticsConsent()` is the gate it
 * has to pass through when it does.
 */
export function CookieConsent() {
  const { t } = useTranslation()
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    setVisible(readConsent() === null)
    const reopen = () => setVisible(true)
    window.addEventListener('bonded:consent-open', reopen)
    return () => window.removeEventListener('bonded:consent-open', reopen)
  }, [])

  const decide = useCallback((analytics: boolean) => {
    writeConsent(analytics)
    setVisible(false)
  }, [])

  if (!visible) return null

  return (
    <div className="consent-bar" role="dialog" aria-modal="false" aria-labelledby="consent-title">
      <div className="consent-panel">
        <div className="consent-copy">
          <h2 id="consent-title">{t('consent.title')}</h2>
          <p>
            {t('consent.body')}{' '}
            <a href="https://bonded.studio/cookie-policy/" target="_blank" rel="noreferrer noopener">
              {t('consent.cookiePolicy')}
            </a>{' '}
            ·{' '}
            <a href="https://bonded.studio/privacy-policy/" target="_blank" rel="noreferrer noopener">
              {t('consent.privacyPolicy')}
            </a>
          </p>
        </div>
        <div className="consent-actions">
          <button type="button" className="consent-button consent-button--accept" onClick={() => decide(true)}>
            {t('consent.accept')}
          </button>
          <button type="button" className="consent-button" onClick={() => decide(false)}>
            {t('consent.reject')}
          </button>
        </div>
      </div>
    </div>
  )
}

/** Withdrawing consent has to be as easy as giving it (GDPR Art. 7(3)). */
export function CookieSettingsLink({ className = '' }: { className?: string }) {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      className={className}
      onClick={() => window.dispatchEvent(new CustomEvent('bonded:consent-open'))}
    >
      {t('consent.settings')}
    </button>
  )
}
